/**
 * Ableitungen für „Wirkung prüfen“ (Board „Preisautomatik · Wirkung prüfen“): welcher Zustand der
 * Dialog hat, welche Tage betroffen sind – dieselbe Zählung wie die Zusammenfassung des Servers
 * (`BuildPreviewResponse`) – und welche Hinweise mit wie vielen Tagen anstehen.
 */
import type {
  PricingConfigurationPreviewDay,
  PricingConfigurationPreviewResponse,
} from '@/shared/api';
import { minimumStayText, warningCodeText } from '../pricingCodeText';

export type PreviewState = 'loading' | 'failed' | 'locked' | 'ready';

export function previewState(input: {
  loading: boolean;
  preview?: PricingConfigurationPreviewResponse;
  failed: boolean;
}): PreviewState {
  if (input.loading) return 'loading';
  if (!input.preview || input.failed) return 'failed';
  return input.preview.previewAttestationId && input.preview.canActivate ? 'ready' : 'locked';
}

export type DayResult = 'Blockiert' | 'Warnung' | 'Änderung' | 'Unverändert';

const changedStay = (day: PricingConfigurationPreviewDay) =>
  day.desiredMinimumStay !== null && day.desiredMinimumStay !== day.confirmedMinimumStay;

/**
 * Blockiert wie in der Serverzählung: übersprungen, von Hand gesperrt oder blockiert – dazu gehört
 * der Konflikt mit dem Höchstaufenthalt (Mindestaufenthalt „Blocked“).
 */
const blocked = (day: PricingConfigurationPreviewDay) =>
  day.rateOutcome === 'Skip' ||
  day.minimumStayOutcome === 'Locked' ||
  day.minimumStayOutcome === 'Blocked';

/**
 * Marken eines Tages. „Blockiert“ und „Warnung“ können zusammen stehen; so ist jede der beiden Zahlen
 * der Zusammenfassung genau die Zahl der so markierten Tage.
 */
export function dayResults(day: PricingConfigurationPreviewDay): DayResult[] {
  const marks: DayResult[] = [];
  if (blocked(day)) marks.push('Blockiert');
  if (day.warningCodes.length > 0) marks.push('Warnung');
  if (marks.length > 0) return marks;
  return [day.rateOutcome === 'Change' || changedStay(day) ? 'Änderung' : 'Unverändert'];
}

/** Nur Tage mit Änderung, Warnung oder Sperre – unveränderte Tage blieben eine lange Liste ohne Aussage. */
export const affectedDays = (days: readonly PricingConfigurationPreviewDay[]) =>
  days.filter((day) => dayResults(day)[0] !== 'Unverändert');

export interface PreviewHint {
  code: string;
  /** Anzahl betroffener Tage; `null` bei Hinweisen ohne Tagesbezug. */
  days: number | null;
  text: string;
  tone: 'amber' | 'grey';
}

const hintText = (code: string) =>
  warningCodeText(code) ?? minimumStayText(code) ?? 'Hinweis des Servers.';

export function previewHints(preview: PricingConfigurationPreviewResponse): PreviewHint[] {
  const counts = new Map<string, number>();
  const add = (code: string) => counts.set(code, (counts.get(code) ?? 0) + 1);
  for (const day of preview.days) {
    day.warningCodes.forEach(add);
    if (day.maximumStayStatus === 'Conflict') add('max_stay_conflict');
  }
  const hints: PreviewHint[] = [...counts].map(([code, days]) => ({
    code,
    days,
    text: hintText(code),
    tone: code.startsWith('manual_') ? 'grey' : 'amber',
  }));
  for (const code of preview.warningCodes) {
    if (!counts.has(code)) hints.push({ code, days: null, text: hintText(code), tone: 'amber' });
  }
  return hints.sort((a, b) => (b.days ?? 0) - (a.days ?? 0));
}

const WEEKDAY = ['So', 'Mo', 'Di', 'Mi', 'Do', 'Fr', 'Sa'];

/** „Fr 23.10.“ – zeitzonensicher, weil das Datum ein Kalendertag ist. */
export function dayLabel(iso: string): string {
  const date = new Date(`${iso.slice(0, 10)}T00:00:00Z`);
  if (Number.isNaN(date.getTime())) return iso;
  const day = String(date.getUTCDate()).padStart(2, '0');
  const month = String(date.getUTCMonth() + 1).padStart(2, '0');
  return `${WEEKDAY[date.getUTCDay()]} ${day}.${month}.`;
}
